/** Page size bounds enforced by the backend list endpoints. */
export const LIST_MAX_SIZE = 200;
export const LIST_DEFAULT_SIZE = 50;

/** Generic paginated response returned by the backend list endpoints. */
export interface Paginated<T> {
  total: number;
  page: number;
  size: number;
  results: T[];
}

export type SortDirection = 'asc' | 'desc';

/** One sort key, serialized as `field:direction` (max three per request). */
export interface SortParam {
  field: string;
  direction: SortDirection;
}

/** Common paging and sorting arguments for list requests. */
export interface PageParams {
  page?: number;
  size?: number;
  sort?: SortParam[];
}

/** Query filters for `GET /projects/`. */
export interface ProjectListQuery extends PageParams {
  archived?: boolean;
  q?: string;
  name?: string;
  description?: string;
  salesOrderNumber?: string;
  statusIds?: number[];
  ownerIds?: string[];
  clientIds?: string[];
  ownerIsNull?: boolean;
  clientIsNull?: boolean;
  createdAtFrom?: string;
  createdAtTo?: string;
  startDateFrom?: string;
  startDateTo?: string;
  endDateFrom?: string;
  endDateTo?: string;
  /** Risk level (e.g. HIGH): projects with a non-completed task or action item at this level. */
  risk?: string;
}

/** Query filters shared by `GET /projects/{id}/items` and `GET /action_items/{id}/items`. */
export interface ItemListQuery extends PageParams {
  archived?: boolean;
  name?: string;
  description?: string;
  phaseIds?: string[];
  ownerIds?: string[];
  assignedToIds?: string[];
  ownerIsNull?: boolean;
  assignedToIsNull?: boolean;
  statusIds?: string[];
  priorityIds?: string[];
  /** Status values such as `COMPLETED`, resolved by the backend against the statuses table. */
  statusValues?: string[];
  /** Priority values such as `HIGH`, resolved by the backend against the priorities table. */
  priorityValues?: string[];
  /** Free-text search across name and description. */
  search?: string;
  /** Risk level (e.g. HIGH) matched against the item's risk. */
  risk?: string;
  /** Rooms the item is placed in (any of). */
  roomIds?: string[];
  createdAtFrom?: string;
  createdAtTo?: string;
  startDateFrom?: string;
  startDateTo?: string;
  dueDateFrom?: string;
  dueDateTo?: string;
}

/** Maximum number of sort keys the backend accepts for item lists. */
export const MAX_SORT_KEYS = 3;

/** Default item-list sort, matching the backend default (created_at:desc). */
export const DEFAULT_ITEM_SORT: SortParam[] = [{ field: 'created_at', direction: 'desc' }];

/**
 * Applies a click on a sortable column to the sort keys: a column already in the list flips its direction;
 * a new column is appended as ascending. Only the last MAX_SORT_KEYS keys are kept.
 */
export const toggleSortKey = (keys: SortParam[], field: string): SortParam[] => {
  const existing = keys.find((key) => key.field === field);
  if (existing) {
    return keys.map((key) =>
      key.field === field ? { field, direction: key.direction === 'asc' ? 'desc' : 'asc' } : key
    );
  }
  return [...keys, { field, direction: 'asc' as SortDirection }].slice(-MAX_SORT_KEYS);
};

/**
 * Applies a header click. A plain click makes the column the only sort key, or flips it when it already is.
 * A shift-click adds the column to the keys instead (see toggleSortKey).
 */
export const applySortClick = (keys: SortParam[], field: string, additive: boolean): SortParam[] => {
  if (additive) return toggleSortKey(keys, field);
  if (keys.length === 1 && keys[0].field === field) {
    return [{ field, direction: keys[0].direction === 'asc' ? 'desc' : 'asc' }];
  }
  return [{ field, direction: 'asc' }];
};

/** Validates sort keys read back from localStorage: one to three keys, known fields, asc or desc. */
export const isStoredItemSortKeys = (value: unknown): value is SortParam[] =>
  Array.isArray(value) &&
  value.length >= 1 &&
  value.length <= MAX_SORT_KEYS &&
  value.every((key) =>
    typeof key === 'object' && key !== null &&
    (ACTION_ITEM_SORT_FIELDS as readonly string[]).includes((key as SortParam).field) &&
    ((key as SortParam).direction === 'asc' || (key as SortParam).direction === 'desc')
  );

export const isDefaultItemSort = (keys: SortParam[]): boolean =>
  JSON.stringify(keys) === JSON.stringify(DEFAULT_ITEM_SORT);

/** Arrow and position for a column header, e.g. "↑" or "↓2" when several keys are active. */
export const sortIndicator = (keys: SortParam[], field: string): string => {
  const index = keys.findIndex((key) => key.field === field);
  if (index === -1) return '';
  const arrow = keys[index].direction === 'asc' ? '↑' : '↓';
  return keys.length > 1 ? `${arrow}${index + 1}` : arrow;
};

/** Sort fields accepted by the backend, per resource. */
export const PROJECT_SORT_FIELDS = ['name', 'created_at', 'updated_at', 'start_date', 'end_date', 'status_id', 'risk_level'] as const;
export const ACTION_ITEM_SORT_FIELDS = ['created_at', 'start_date', 'due_date', 'status', 'priority', 'risk'] as const;
